import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { money } from '../../lib/format.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { AutoTextarea } from '../proposal/CanvasBlock.js'
import { Modal, ModalCloseButton } from '../Modal.js'
import { Button, ErrorState, Field, SkeletonRows, Textarea } from '../ui.js'
import type { Job } from './types.js'

/**
 * Raising the first quote or invoice on a job.
 *
 * `quotes.create` and `invoices.create` have existed since the schema did with
 * no caller in the client, so until now every document in FieldOps arrived
 * from ServiceM8, a seed or a test. This is the screen that was missing.
 *
 * **The lines arrive as a suggestion and nothing more.** `catalog.linesForJob`
 * reads what the job's services carry — the default lines configured against
 * each `ServiceType` in Settings, priced against the client's tier — and drops
 * them into an editable table with the service that produced each one named
 * beside it. The office edits, deletes, adds, and the document is created from
 * what is on the screen when they press the button. Nothing is written from a
 * suggestion, which is rule 12 applied to money rather than to answers.
 *
 * A job with no services, or services with nothing configured, opens on one
 * blank line. That is the honest starting point and it is still a working
 * screen, which is the part that matters.
 */
export function RaiseDocumentPopup({
  kind,
  job,
  open,
  onClose,
}: {
  kind: 'quote' | 'invoice'
  job: Job
  open: boolean
  onClose: () => void
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="raise-document-title" className="max-w-[640px]">
      {open ? <Body kind={kind} job={job} onClose={onClose} /> : null}
    </Modal>
  )
}

/** A line as this editor holds it: strings, because somebody is typing. */
interface Draft {
  key: string
  description: string
  quantity: string
  /** Dollars as typed. Converted to minor units on save, never before. */
  unitPrice: string
  /** A suggested price that is inc GST, carried until the price is edited. */
  unitPriceIncTax: number | null
  taxRateId: string | null
  materialId: string | null
  /** Which service suggested it, for the label. Null once it is hand-typed. */
  from: string | null
}

function Body({
  kind,
  job,
  onClose,
}: {
  kind: 'quote' | 'invoice'
  job: Job
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const [drafts, setDrafts] = useState<Draft[] | null>(null)
  const [terms, setTerms] = useState('')

  const suggestions = useQuery(trpc.catalog.linesForJob.queryOptions({ jobId: job.id }))

  // Seeded once, when the suggestions land. Not on every render: the office is
  // typing into these rows and a refetch must not throw the edits away.
  useEffect(() => {
    if (!suggestions.data || drafts !== null) return

    const seeded = suggestions.data.flatMap((service) =>
      service.lines.map((line, index) => ({
        key: `${service.jobServiceId}-${index}`,
        description: line.description,
        quantity: line.quantity,
        unitPrice: (line.unitPrice / 100).toString(),
        unitPriceIncTax: line.unitPriceIncTax,
        taxRateId: line.taxRateId,
        materialId: line.materialId,
        from: service.serviceName,
      })),
    )

    setDrafts(seeded.length > 0 ? seeded : [blank()])
  }, [suggestions.data, drafts])

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.quotes.pathFilter())
    void queryClient.invalidateQueries(trpc.invoices.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const options = {
    onSuccess: () => {
      invalidate()
      onClose()
    },
  }
  const createQuote = useMutation(trpc.quotes.create.mutationOptions(options))
  const createInvoice = useMutation(trpc.invoices.create.mutationOptions(options))
  const create = kind === 'quote' ? createQuote : createInvoice

  const rows = drafts ?? []
  const usable = rows.filter((row) => row.description.trim() !== '')
  const total = usable.reduce((sum, row) => sum + amountOf(row), 0)

  const patch = (key: string, next: Partial<Draft>) =>
    setDrafts((current) =>
      (current ?? []).map((row) => (row.key === key ? { ...row, ...next } : row)),
    )

  const submit = () => {
    const lines = usable.map((row, index) => ({
      description: row.description.trim(),
      quantity: row.quantity.trim() === '' ? '1' : row.quantity.trim(),
      unitPrice: centsOf(row.unitPrice),
      unitPriceIncTax: row.unitPriceIncTax,
      unitCost: 0,
      discountBasisPoints: 0,
      taxRateId: row.taxRateId,
      isOptional: false,
      sortOrder: index,
      materialId: row.materialId,
    }))

    const note = terms.trim() === '' ? null : terms.trim()

    if (kind === 'quote') {
      createQuote.mutate({ jobId: job.id, lines, termsText: note, discountAmount: 0 })
    } else {
      createInvoice.mutate({
        clientId: job.clientId,
        jobId: job.id,
        lines,
        notesToCustomer: note,
        discountAmount: 0,
      })
    }
  }

  return (
    <>
      <div className="flex items-center gap-3 border-b border-ink-200 px-4 py-3">
        <h2 id="raise-document-title" className="text-base font-semibold text-ink-900">
          {kind === 'quote' ? 'New quote' : 'New invoice'}
        </h2>
        <span className="text-xs text-ink-500">{job.client.name}</span>
        <div className="ml-auto">
          <ModalCloseButton onClose={onClose} />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        {suggestions.isPending ? (
          <SkeletonRows rows={3} />
        ) : suggestions.error ? (
          <ErrorState
            message={errorMessage(suggestions.error)}
            onRetry={() => void suggestions.refetch()}
          />
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <div className="grid grid-cols-[1fr_60px_84px_92px_28px] gap-2 px-1 text-[10px] font-bold tracking-[0.06em] text-ink-500 uppercase">
                <span>Item</span>
                <span className="text-right">Qty</span>
                <span className="text-right">Unit $</span>
                <span className="text-right">Amount</span>
                <span className="sr-only">Remove</span>
              </div>

              {rows.map((row, index) => (
                <div
                  key={row.key}
                  className="grid grid-cols-[1fr_60px_84px_92px_28px] items-start gap-2 rounded-md px-1 py-1 hover:bg-ink-50"
                >
                  <span className="flex min-w-0 flex-col">
                    <AutoTextarea
                      label={`Line ${index + 1} description`}
                      value={row.description}
                      placeholder="Description"
                      onChange={(description) => patch(row.key, { description })}
                      className="min-w-0 border-0 text-[13px] font-medium text-ink-900 placeholder:text-ink-400"
                    />
                    {row.from ? (
                      <span className="truncate text-[11px] text-ink-400">{row.from}</span>
                    ) : null}
                  </span>
                  <input
                    aria-label={`Line ${index + 1} quantity`}
                    inputMode="decimal"
                    value={row.quantity}
                    onChange={(event) => patch(row.key, { quantity: event.target.value })}
                    className="w-full border-0 bg-transparent text-right text-[13px] tabular-nums text-ink-700 outline-none"
                  />
                  <input
                    aria-label={`Line ${index + 1} unit price`}
                    inputMode="decimal"
                    value={row.unitPrice}
                    onChange={(event) => patch(row.key, { unitPrice: event.target.value, unitPriceIncTax: null })}
                    className="w-full border-0 bg-transparent text-right text-[13px] tabular-nums text-ink-700 outline-none"
                  />
                  <span className="text-right text-[13px] font-medium tabular-nums text-ink-900">
                    {money(amountOf(row))}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove line ${index + 1}`}
                    onClick={() =>
                      setDrafts((current) => (current ?? []).filter((r) => r.key !== row.key))
                    }
                    className="flex size-7 items-center justify-center text-ink-400 hover:text-ink-800"
                  >
                    <Icon name="close" className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-2">
              <Button
                size="sm"
                onClick={() => setDrafts((current) => [...(current ?? []), blank()])}
              >
                Add a line
              </Button>
              <span className="text-[13px] text-ink-600">
                Total <span className="font-semibold tabular-nums text-ink-900">{money(total)}</span>
              </span>
            </div>

            <Field
              label={kind === 'quote' ? 'Note on the quote' : 'Note to the customer'}
              hint="Optional — the default terms are used when this is blank"
              htmlFor="raise-document-terms"
            >
              <Textarea
                id="raise-document-terms"
                rows={3}
                value={terms}
                onChange={(event) => setTerms(event.target.value)}
              />
            </Field>

            {create.error ? <ErrorState message={errorMessage(create.error)} /> : null}
          </>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-ink-200 px-4 py-3">
        <Button size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          loading={create.isPending}
          disabled={usable.length === 0}
          onClick={submit}
        >
          {kind === 'quote' ? 'Create the quote' : 'Create the invoice'}
        </Button>
      </div>
    </>
  )
}

function blank(): Draft {
  return {
    key: `new-${Math.random().toString(36).slice(2)}`,
    description: '',
    quantity: '1',
    unitPrice: '0',
    unitPriceIncTax: null,
    taxRateId: null,
    materialId: null,
    from: null,
  }
}

/** Dollars as typed to integer minor units. Never a float in the payload. */
function centsOf(dollars: string): number {
  const value = Number(dollars.trim())
  return Number.isFinite(value) ? Math.round(value * 100) : 0
}

/**
 * The row's amount while somebody is typing.
 *
 * Tax-exclusive, and no attempt at the tax: the server prices the line on
 * save with `money.calculateLine` and this is only here so a typed figure
 * shows an amount rather than nothing. The document's own totals come back
 * from the server.
 */
function amountOf(row: Draft): number {
  const quantity = Number(row.quantity.trim() === '' ? '1' : row.quantity)
  if (!Number.isFinite(quantity)) return 0

  return Math.round((Math.round(quantity * 10_000) * centsOf(row.unitPrice)) / 10_000)
}
